import { deepEquals } from "../deepEquals";

/**
 * Merges multiple JavaScript objects or arrays.
 * If the parameters are arrays, it concatenates them removing duplicates.
 * If the parameters are objects, it merges them. If the same key exists in multiple objects,
 * and the value of that key in both objects is also an object, then it recursively merges those nested objects.
 *
 * @param {...(Object|Array)} objects - The objects or arrays to merge.
 * @returns {Object|Array} - The merged object or array.
 */
export const merge = (...objects) => {
  let result;
  const validObjects = objects.filter((obj) => obj != null);

  if (validObjects.length === 0) {
    result = {};
  } else if (validObjects.length === 1) {
    result = validObjects[0];
  } else {
    const [object1, object2, ...rest] = validObjects;

    if (Array.isArray(object1) && Array.isArray(object2)) {
      const combinedArray = [...object1, ...object2];
      const objectItems = combinedArray.filter((item) => typeof item === "object" && item !== null);
      const primitiveItems = combinedArray.filter(
        (item) => typeof item !== "object" || item === null
      );
      const mergedArray = [...deduplicateArrayOfObjects(objectItems), ...new Set(primitiveItems)];

      result = rest.length ? merge(mergedArray, ...rest) : mergedArray;
    } else {
      const mergedObject = Object.entries(object2 || {}).reduce(
        (accumulatedObject, [object2Key, object2Value]) => {
          if (
            Object.keys(accumulatedObject).includes(object2Key) &&
            typeof object2Value === "object" &&
            object2Value !== null &&
            typeof accumulatedObject[object2Key] === "object" &&
            accumulatedObject[object2Key] !== null
          ) {
            accumulatedObject[object2Key] = merge(accumulatedObject[object2Key], object2Value);
          } else {
            accumulatedObject[object2Key] = object2Value;
          }

          return accumulatedObject;
        },
        { ...object1 }
      );

      result = rest.length ? merge(mergedObject, ...rest) : mergedObject;
    }
  }

  return result;

  function deduplicateArrayOfObjects(array) {
    return array.reduce((unique, item) => {
      const exists = unique.some((existingItem) => deepEquals(existingItem, item));

      if (!exists) {
        unique.push(item);
      }

      return unique;
    }, []);
  }
};
